// store/resources.js
import { ElMessage, MessageHandler } from "element-plus";
import { defineStore } from "pinia";
import { getEnvConfig } from "~/services/appConfig";
import { showError } from "~/utils/handleAPIError";
import { hubManager } from "~/utils/hubManager";
import { getMessageTime } from "~/utils/miscUtils";

// Module-level holder for the "self" hub PdfMsg subscription.
// The store owns the listener because it owns the progress message state
// that the messages feed (elMessageHandler + the toasts shown on PdfMsg).
let pdfProgressUnsubscribe: (() => void) | null = null;

export const usePdfLoadingStore = defineStore("pdfLoadingStore", {
  state: () => ({
    loadedComponents: 0,
    totalComponents: 0,
    workflowGuid: "",
    errors: [] as string[],
    messageHandler: null as MessageHandler | null,
  }),
  actions: {
    componentBeingLoaded() {
      this.totalComponents++;
      // console.log(
      //   `Registered component. Total components: ${this.totalComponents}`,
      // );
    },
    componentLoaded(error = null) {
      if (error !== null) {
        this.errors.push(error ?? "Unknown error");
      }
      this.loadedComponents++;
      // console.log(
      //   `Component loaded. Loaded components: ${this.loadedComponents}, Total components: ${this.totalComponents}`,
      // );
    },
    setWorkflowGuid(guid: string) {
      this.workflowGuid = guid;
    },

    getWorkflowGuid() {
      return this.workflowGuid;
    },

    setElMessageHandler(handler: MessageHandler | null) {
      this.messageHandler = handler;
    },

    /**
     * Ensures a single subscription to PdfMsg on the shared "self" hub.
     * Called (and awaited) from makePdf so that the handler is guaranteed to be
     * attached *before* we trigger the iframe load that causes the server to
     * start sending PdfMsg updates (step 2, etc.).
     */
    async ensurePdfProgressListener() {
      if (pdfProgressUnsubscribe) return;

      const handler = (obj: any) => {
        console.log("PDF message received:", obj);
        const isBusy = obj.isBusy;
        const msg = obj.msg;

        const oldMessage = this.elMessageHandler;
        if (oldMessage) {
          oldMessage.close();
          this.setElMessageHandler(null);
        }

        if (isBusy) {
          const elMsg = ElMessage.info({
            message: msg,
            duration: 0, // keep until next message or manual close
            showClose: false,
          });
          this.setElMessageHandler(elMsg);
        } else {
          const elMsg = ElMessage.info({
            message: msg,
            duration: getMessageTime(msg),
            showClose: true,
          });
          this.setElMessageHandler(elMsg);
        }
      };

      try {
        const unsub = await hubManager.subscribe(
          "self",
          "/self",
          "PdfMsg",
          handler,
        );
        pdfProgressUnsubscribe = unsub;
      } catch (err) {
        console.warn("[pdfLoadingStore] Failed to subscribe to PdfMsg:", err);
      }
    },

    cleanupPdfProgressListener() {
      if (pdfProgressUnsubscribe) {
        try {
          pdfProgressUnsubscribe();
        } catch {
          /* ignore */
        }
        pdfProgressUnsubscribe = null;
      }
    },

    reset() {
      this.loadedComponents = 0;
      this.totalComponents = 0;
      this.errors = [];
      this.workflowGuid = "";
      // We intentionally do *not* auto-clean the PDF listener here —
      // once a user has generated a PDF this session, keep the listener
      // so subsequent makePdf calls get progress. Call cleanup explicitly
      // on full app unmount if desired.
    },

    async makePdf(
      pdfCode?: string,
      workflowGuid: string = "",
      connectionId?: string,
    ) {
      // Ensure the PdfMsg listener is attached *before* we kick off the iframe
      // navigation that causes the backend to send "step 2", completion, etc.
      await this.ensurePdfProgressListener();

      // After subscribing to "self", the ConnectionId event from the server may still
      // be in flight. The ID we put in localStorage is what the iframe PDF viewer will
      // pass to the backend. The backend then does:
      //   IClientProxy hub = _selfHub.Clients.Client(settings.ConnectionId.ToString());
      //   await hub.SendAsync("PdfMsg", { Msg = "Creating PDF, step 2 of 2...", IsBusy = true });
      // If we pass a stale ID (e.g. from sitewide hub, or before the self connection
      // reported its ID), the server will target a client that doesn't exist for SelfHub,
      // so no "PdfMsg" ever appears on the wire for the browser.
      // Use the ConnectionId that belongs to the "self" hub (the one we just
      // ensured has the PdfMsg listener). This is stored per-hub in hubManager
      // so that other hubs joined by the viewer (e.g. "workflowInstance") don't
      // pollute the ID we pass to the backend for PDF progress messages.
      let effectiveConnectionId =
        hubManager.getHubConnectionId("self") ??
        connectionId ??
        hubManager.ConnectionId ??
        "";
      const start = Date.now();
      while (!effectiveConnectionId && Date.now() - start < 1500) {
        await new Promise((r) => setTimeout(r, 100));
        effectiveConnectionId =
          hubManager.getHubConnectionId("self") ??
          connectionId ??
          hubManager.ConnectionId ??
          "";
      }

      const pdfFrame = document.getElementById("pdfFrame") as HTMLIFrameElement;
      if (!pdfFrame) {
        showError("PDF frame not found");
        return;
      }

      pdfFrame.onload = () => {
        pdfFrame.onload = null; // remove the onload handler to prevent it from being called again

        // needed by the iFrame to know which connection get the server to respond to
        localStorage.setItem("hubConnectionId", effectiveConnectionId);

        this.setElMessageHandler(
          ElMessage.info({
            message: "Creating PDF, step 1 of 2...",
            duration: 0,
            showClose: true,
          }),
        );
        // fill with the one that will generate the PDF
        // Use hash route for hash-history mode (HAVN uses createWebHashHistory).
        // Without the #/, the iframe loads at a path that hash-router will not match (stays at root redirect).
        const base = (getEnvConfig().entraRedirect || "").replace(/\/$/, "");
        const url = `${base}/#/pdf/${workflowGuid}${pdfCode ? `/${pdfCode}` : ""}`;
        pdfFrame.src = url;
      };

      pdfFrame.src = "about:blank"; // clear the frame first, the onload will trigger when the new src is loaded
    },
  },
  getters: {
    allLoaded: (state) => {
      const loaded =
        state.loadedComponents === state.totalComponents &&
        state.totalComponents > 0;

      // console.log(
      //   `Checking allLoaded: ${loaded}. Components: ${state.loadedComponents} / ${state.totalComponents}`,
      // );

      return loaded;
    },
    elMessageHandler: (state) => state.messageHandler,
  },
});
